import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Upload, X, Loader2, Wand2 } from 'lucide-react';
import BgCompareModal from './BgCompareModal';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
const ACCEPTED_MIMES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * BgImageMenu — floating action menu offering three ways to control a
 * carousel page's background image:
 *
 *   🎲 AI 重新生成    → opens a text-prompt sub-modal, POSTs /bg/generate
 *   📤 上傳我的圖片   → hidden <input type=file>, POSTs /bg/upload
 *   ❌ 移除背景       → DELETE /bg (with confirm if a bg exists)
 *
 * Thanks to the decoupled `bg_image` field (Phase 1), all three ops touch
 * only the image slot — page HTML stays intact so text layout is safe.
 *
 * Props:
 *   open, onClose, carouselId, page (with .index and .bg_image),
 *   anchorRect (getBoundingClientRect of the trigger button, for positioning)
 *   onPageUpdate(index, {bg_image, ...})
 */
export default function BgImageMenu({
  open,
  onClose,
  carouselId,
  page,
  anchorRect,
  onPageUpdate,
  onCreditsCharged,
}) {
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptDraft, setPromptDraft] = useState('');
  const [busy, setBusy] = useState(null); // 'ai' | 'upload' | 'clear'
  const [compare, setCompare] = useState(null); // {previewId, original, adjusted, creditsCharged}
  const [confirmBusy, setConfirmBusy] = useState(null); // 'original' | 'adjusted'
  const fileRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPromptOpen(false);
      setPromptDraft('');
      setCompare(null);
      setConfirmBusy(null);
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (compare || busy === 'upload') return; // keep alive while comparing / adjusting
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        // Ignore clicks on the file input which pops outside DOM
        onClose();
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onClose, compare, busy]);

  if (!open || !page || !anchorRect) return null;

  const hasBg = !!page.bg_image;

  const runAI = async () => {
    const p = promptDraft.trim();
    if (!p || p.length < 3) {
      toast.error('請輸入圖片描述（至少 3 個字）');
      return;
    }
    setBusy('ai');
    try {
      const r = await axios.post(
        `${API}/api/carousel/${carouselId}/page/${page.index}/bg/generate`,
        { prompt: p },
        { timeout: 180000 },
      );
      onPageUpdate?.(page.index, { bg_image: r.data.bg_image });
      if (onCreditsCharged && r.data.credits_charged) {
        onCreditsCharged(r.data.credits_charged);
      }
      toast.success(`第 ${page.index} 頁背景已重新生成 · 扣除 ${(r.data.credits_charged || 0).toFixed(1)} 點數`);
      onClose();
    } catch (e) {
      toast.error('AI 生圖失敗：' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const runUpload = async (file) => {
    if (!file) return;
    if (!ACCEPTED_MIMES.includes(file.type)) {
      toast.error('只支援 JPG / PNG / WebP 圖片');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error('檔案太大，請控制在 8MB 以內');
      return;
    }
    setBusy('upload');
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(
        `${API}/api/carousel/${carouselId}/page/${page.index}/bg/upload/preview`,
        form,
        { timeout: 200000, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (r.data.applied) {
        // Fallback path: raw upload applied directly (insufficient credits / adjust failed)
        onPageUpdate?.(page.index, { bg_image: r.data.bg_image });
        toast.info(r.data.note || `第 ${page.index} 頁背景已更換`);
        onClose();
      } else {
        if (onCreditsCharged && r.data.credits_charged) {
          onCreditsCharged(r.data.credits_charged);
        }
        setCompare({
          previewId: r.data.preview_id,
          original: r.data.original,
          adjusted: r.data.adjusted,
          creditsCharged: r.data.credits_charged || 0,
        });
      }
    } catch (e) {
      toast.error('上傳失敗：' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const pickCompare = async (choice) => {
    if (!compare) return;
    setConfirmBusy(choice);
    try {
      const r = await axios.post(
        `${API}/api/carousel/${carouselId}/page/${page.index}/bg/upload/confirm`,
        { preview_id: compare.previewId, choice },
        { timeout: 30000 },
      );
      onPageUpdate?.(page.index, { bg_image: r.data.bg_image });
      toast.success(
        choice === 'adjusted'
          ? `第 ${page.index} 頁已套用智能調整後的背景`
          : `第 ${page.index} 頁已套用原圖背景`,
      );
      setCompare(null);
      onClose();
    } catch (e) {
      toast.error('套用失敗：' + (e.response?.data?.detail || e.message));
    } finally {
      setConfirmBusy(null);
    }
  };

  const runClear = async () => {
    if (hasBg && !window.confirm(`確定要移除第 ${page.index} 頁的背景圖片嗎？`)) return;
    setBusy('clear');
    try {
      await axios.delete(
        `${API}/api/carousel/${carouselId}/page/${page.index}/bg`,
        { timeout: 15000 },
      );
      onPageUpdate?.(page.index, { bg_image: null });
      toast.success(`第 ${page.index} 頁背景已移除`);
      onClose();
    } catch (e) {
      toast.error('移除失敗：' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  // Position: anchor to bottom of trigger, keep in viewport
  const menuWidth = 320;
  const menuHeight = promptOpen ? 260 : 200;
  let top = anchorRect.bottom + 6;
  let left = anchorRect.left + anchorRect.width / 2 - menuWidth / 2;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  if (left + menuWidth > vw - 8) left = vw - menuWidth - 8;
  if (left < 8) left = 8;
  if (top + menuHeight > vh - 8) top = anchorRect.top - menuHeight - 6;

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED_MIMES.join(',')}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = ''; // reset so same file can be re-picked
          if (f) runUpload(f);
        }}
        data-testid={`bg-menu-file-input-${page.index}`}
      />

      <div
        ref={menuRef}
        className="fixed z-[60] rounded-xl border border-obsidian-600 bg-obsidian-950/95 backdrop-blur-sm shadow-2xl p-2 space-y-1"
        style={{ top, left, width: menuWidth }}
        data-testid={`bg-menu-${page.index}`}
      >
        {!promptOpen && (
          <>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => setPromptOpen(true)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm text-obsidian-100 hover:bg-lime-500/10 hover:text-lime-400 transition-colors disabled:opacity-40"
              data-testid={`bg-menu-ai-${page.index}`}
            >
              <Wand2 className="w-4 h-4" />
              <div className="flex-1">
                <div className="font-medium">AI 重新生成</div>
                <div className="text-[10px] text-obsidian-500">用文字描述生成新背景 · 按實際用量計費</div>
              </div>
            </button>

            <button
              type="button"
              disabled={!!busy}
              onClick={() => fileRef.current?.click()}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm text-obsidian-100 hover:bg-lime-500/10 hover:text-lime-400 transition-colors disabled:opacity-40"
              data-testid={`bg-menu-upload-${page.index}`}
            >
              {busy === 'upload' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              <div className="flex-1">
                <div className="font-medium">
                  {busy === 'upload' ? '智能調整中…約 30 秒' : '上傳我自己的圖片'}
                </div>
                <div className="text-[10px] text-obsidian-500">
                  {busy === 'upload'
                    ? '正在依版面配色調整你的圖片'
                    : '上傳後自動調整成合適底圖 · 按實際用量計費'}
                </div>
              </div>
            </button>

            <button
              type="button"
              disabled={!!busy || !hasBg}
              onClick={runClear}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm text-obsidian-100 hover:bg-red-500/10 hover:text-red-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid={`bg-menu-clear-${page.index}`}
            >
              {busy === 'clear' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <X className="w-4 h-4" />
              )}
              <div className="flex-1">
                <div className="font-medium">移除背景</div>
                <div className="text-[10px] text-obsidian-500">
                  {hasBg ? '恢復到純文字版面' : '目前沒有背景'}
                </div>
              </div>
            </button>
          </>
        )}

        {promptOpen && (
          <div className="p-2 space-y-2" data-testid={`bg-menu-ai-prompt-${page.index}`}>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-3.5 h-3.5 text-lime-400" />
              <span className="text-[11px] text-lime-400 font-medium">
                AI 圖像描述 · 第 {page.index} 頁
              </span>
            </div>
            <textarea
              value={promptDraft}
              onChange={(e) => setPromptDraft(e.target.value)}
              placeholder="例：黃昏光線的森林，微霧、暖色調、柔和景深"
              rows={4}
              disabled={busy === 'ai'}
              className="w-full bg-obsidian-900 border border-obsidian-700 focus:border-lime-500 rounded-lg text-white text-sm p-2 outline-none resize-none disabled:opacity-40"
              data-testid={`bg-menu-ai-textarea-${page.index}`}
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPromptOpen(false)}
                disabled={busy === 'ai'}
                className="flex-1 h-8 rounded-md border border-obsidian-600 text-obsidian-300 text-xs hover:border-obsidian-500 transition-colors"
              >
                返回
              </button>
              <button
                type="button"
                onClick={runAI}
                disabled={busy === 'ai' || promptDraft.trim().length < 3}
                className="flex-1 h-8 rounded-md bg-lime-500 hover:bg-lime-400 text-obsidian-950 text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40 transition-colors"
                data-testid={`bg-menu-ai-run-${page.index}`}
              >
                {busy === 'ai' ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    生成中… ~30 秒
                  </>
                ) : (
                  <>
                    <Wand2 className="w-3 h-3" />
                    生成
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      <BgCompareModal
        open={!!compare}
        pageIndex={page.index}
        original={compare?.original}
        adjusted={compare?.adjusted}
        creditsCharged={compare?.creditsCharged || 0}
        busy={confirmBusy}
        onPick={pickCompare}
        onCancel={() => {
          setCompare(null);
          onClose();
        }}
      />
    </>
  );
}
